import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, Accordion, AccordionSummary, AccordionDetails,
    TextField, Box, Button, IconButton, Grid, CircularProgress, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from '../context/SnackbarContext';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    DragHandle as DragHandleIcon,
    Send as SendIcon
} from '@mui/icons-material';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../Auth';
import api from '../api/axiosInstance';

const DEFAULT_STUDENT_SCHEMA = [
    { id: 'id_1', label: 'Student ID' }, { id: 'id_2', label: 'Student Title' },
    { id: 'id_3', label: 'Student Preferred Name' }, { id: 'id_4', label: 'Student Given Name 1' },
    { id: 'id_5', label: 'Student Surname' }, { id: 'id_6', label: 'Student Campus' },
    { id: 'id_7', label: 'Student Year Level' }, { id: 'id_8', label: 'Student Homeroom Teacher' },
    { id: 'id_9', label: 'Student School House' }, { id: 'id_10', label: 'Boarder' },
    { id: 'id_11', label: 'Student Entry Date' }, { id: 'id_12', label: 'Student Leaving Date' },
    { id: 'id_13', label: 'Indigenous Student' }, { id: 'id_14', label: 'Student Active' },
    { id: 'id_15', label: 'Contact 1 ID' }, { id: 'id_16', label: 'Contact 1 Relation (Mother/Father/etc)' },
    { id: 'id_17', label: 'Contact 1 Salutation' }, { id: 'id_18', label: 'Contact 1 Given Name' },
    { id: 'id_19', label: 'Contact 1 Surname' }, { id: 'id_20', label: 'Contact 1 Email' },
    { id: 'id_21', label: 'Contact 2 ID' }, { id: 'id_22', label: 'Contact 2 Relation (Grandmother/Uncle/etc)' },
    { id: 'id_23', label: 'Contact 2 Salutation' }, { id: 'id_24', label: 'Contact 2 Given Name' },
    { id: 'id_25', label: 'Contact 2 Surname' }, { id: 'id_26', label: 'Contact 2 Email' }
];

const DEFAULT_AWARD_SCHEMA = [
    { id: 'a_1', label: 'Award Name' }, { id: 'a_2', label: 'Category' },
    { id: 'a_3', label: 'Year Level' }, { id: 'a_4', label: 'Criteria' }
];

export default function SystemSettings() {
    const { user } = useAuth();
    const showSnackbar = useSnackbar();
    const [settings, setSettings] = useState({
        ceremony_open_delay_weeks: '',
        ceremony_cutoff_delay_weeks: '',
        student_import_schema: [],
        award_import_schema: [],
        wwcc_alert_settings: [],
        wwcc_report_schedule: []
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const lastAddedId = useRef(null);
    const inputRefs = useRef({});
    const [testEmail, setTestEmail] = useState('');
    const [testing, setTesting] = useState(false);
    const [testDays, setTestDays] = useState(15);

    // Helper to determine if we are in "Global View"
    // If user is superuser and no tenant is selected, this is true.
    const isSuperuser = user?.role === 'superuser'
    const isGlobalView = isSuperuser && !user?.tenant_id;

    const isModuleEnabled = (moduleName) => {
        // 1. If we are in Global View (no tenant selected), hide all modules
        if (isGlobalView) return false;

        // 2. If a tenant is selected:
        // We check if the module is actually enabled for THIS specific tenant.
        // This applies to both admins AND superusers.
        return user?.enabled_modules?.includes(moduleName);
    };
    const accordionStyle = {
        borderRadius: '4px !important', // Tightens the corners
        mb: 2,
        boxShadow: 1, // Optional: slightly lighter shadow often looks cleaner with sharper corners
        '&:before': {
            display: 'none',
        },
    };
    const SCHEDULE_TYPES = [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
    ];
    const DAYS_OF_WEEK = [
        { value: '1', label: 'Monday' }, { value: '2', label: 'Tuesday' }, { value: '3', label: 'Wednesday' },
        { value: '4', label: 'Thursday' }, { value: '5', label: 'Friday' }, { value: '6', label: 'Saturday' },
        { value: '0', label: 'Sunday' }
    ];
    const reportSchedule = settings.wwcc_report_schedule || [];

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/settings`);

                // This is now the clean object from your backend logs
                const data = res.data;

                // Axios puts the parsed JSON body in 'data'
                setSettings({
                    ceremony_open_delay_weeks: data.ceremony_open_delay_weeks || '4',
                    ceremony_cutoff_delay_weeks: data.ceremony_cutoff_delay_weeks || '2',
                    student_import_schema: (data.student_import_schema?.length > 0)
                        ? data.student_import_schema
                        : DEFAULT_STUDENT_SCHEMA,
                    award_import_schema: (data.award_import_schema?.length > 0)
                        ? data.award_import_schema
                        : DEFAULT_AWARD_SCHEMA,
                    wwcc_alert_settings: data.wwcc_alert_settings || [],
                    wwcc_report_schedule: data.wwcc_report_schedule || []
                });

            } catch (err) {
                // Handle Axios errors properly
                const errorMessage = err.response?.data?.error || err.message || "Failed to load settings";
                showSnackbar(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [showSnackbar]);

    useEffect(() => {
        if (lastAddedId.current && inputRefs.current[lastAddedId.current]) {
            inputRefs.current[lastAddedId.current].focus();
            lastAddedId.current = null;
        }
    }, [settings]);

    const handleSchemaLabelChange = (type, id, newLabel) => {
        const updatedSchema = settings[type].map(col =>
            col.id === id ? { ...col, label: newLabel } : col
        );
        setSettings({ ...settings, [type]: updatedSchema });
    };

    const addRow = (type) => {
        const prefix = type === 'student_import_schema' ? 'stu' : 'awd';
        const newId = `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        lastAddedId.current = newId;
        setSettings(prev => ({ ...prev, [type]: [...prev[type], { id: newId, label: '' }] }));
    };

    const deleteRow = (type, id) => {
        setSettings({ ...settings, [type]: settings[type].filter(col => col.id !== id) });
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination ||
            source.droppableId !== destination.droppableId ||
            (destination.droppableId === source.droppableId && destination.index === source.index)
        ) return;

        const listKey = source.droppableId;
        const newItems = Array.from(settings[listKey]);
        const [reorderedItem] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, reorderedItem);

        setSettings(prev => ({ ...prev, [listKey]: newItems }));
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // 1. Create a clean payload where all objects/arrays are stringified
            const formattedSettings = {};
            Object.keys(settings).forEach(key => {
                const value = settings[key];
                // If it's an array or object, stringify it for the TEXT column in DB
                formattedSettings[key] = (value && typeof value === 'object')
                    ? JSON.stringify(value)
                    : value;
            });

            // Simply pass the data; the interceptor adds the token
            await api.post('/settings', { settings: formattedSettings });

            showSnackbar('Settings saved!', 'success');
            setExpanded(false);
        } catch (err) {
            showSnackbar('Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const renderSchemaEditor = (type, title, description) => (
        <Accordion
            expanded={expanded === type}
            onChange={handleAccordionChange(type)}
            sx={accordionStyle}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {description}
                </Typography>

                <Droppable
                    droppableId={type}
                    type={type === 'student_import_schema' ? 'STUDENT' : 'AWARD'}
                >
                    {(provided, snapshot) => (
                        <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            sx={{
                                minHeight: '50px',
                                // bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                bgcolor: 'transparent',
                                borderRadius: 1
                            }}
                        >
                            {settings[type].map((col, index) => (
                                <Draggable key={col.id} draggableId={col.id} index={index}>
                                    {(provided, snapshot) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 0.5,
                                                p: 0,
                                                // Background set to paper (white) during drag
                                                bgcolor: 'background.paper',
                                                boxShadow: snapshot.isDragging ? 3 : 0,
                                                borderRadius: 1,
                                                ...provided.draggableProps.style
                                            }}
                                        >
                                            <Box {...provided.dragHandleProps} sx={{ display: 'flex', px: 0.5, cursor: 'grab' }}>
                                                <DragHandleIcon color="action" fontSize="small" />
                                            </Box>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={col.label}
                                                inputRef={el => inputRefs.current[col.id] = el}
                                                onChange={(e) => handleSchemaLabelChange(type, col.id, e.target.value)}
                                                placeholder="Heading Name"
                                                sx={{
                                                    '& .MuiInputBase-root': { py: 0 },
                                                    bgcolor: '#ffffff'
                                                }}
                                            />
                                            <IconButton onClick={() => deleteRow(type, col.id)} color="error" size="small">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => addRow(type)}
                    variant="text"
                    size="small"
                    sx={{ mt: 1, fontWeight: 'bold' }}
                >
                    Add Heading
                </Button>
            </AccordionDetails>
        </Accordion>
    );

    const addNotification = () => {
        setSettings(prev => ({
            ...prev,
            wwcc_alert_settings: [
                ...prev.wwcc_alert_settings,
                { id: Date.now(), value: 30, unit: 'days' }
            ]
        }));
    };

    const updateNotification = (id, field, newValue) => {
        const updated = settings.wwcc_alert_settings.map(n =>
            n.id === id ? { ...n, [field]: newValue } : n
        );
        setSettings({ ...settings, wwcc_alert_settings: updated });
    };

    const deleteNotification = (id) => {
        setSettings({
            ...settings,
            wwcc_alert_settings: settings.wwcc_alert_settings.filter(n => n.id !== id)
        });
    };

    const triggerEmailTest = async (type) => {
        if (!testEmail) {
            showSnackbar('Please enter a test email address', 'warning');
            return;
        }

        setTesting(true);
        try {
            const { data } = await api.post('/settings/test-email', {
                testEmail: testEmail,
                type: type,
                days: testDays // Send the dynamic days value
            });

            showSnackbar(data.message || 'Test email triggered!', 'success');
        } catch (error) {
            console.error("Test Email Error:", error);
            showSnackbar(error.response?.data?.error || 'Failed to send test email', 'error');
        } finally {
            setTesting(false);
        }
    };

    const addSchedule = () => {
        const newSched = {
            id: Date.now(),
            type: 'weekly',
            day: '1',
            time: '08:00'
        };
        setSettings({
            ...settings,
            wwcc_report_schedule: [...(settings.wwcc_report_schedule || []), newSched]
        });
    };

    const updateSchedule = (id, field, value) => {
        const currentSchedule = settings.wwcc_report_schedule || [];
        const updated = currentSchedule.map(s =>
            s.id === id ? { ...s, [field]: value } : s
        );
        setSettings(prev => ({ ...prev, wwcc_report_schedule: updated }));
    };

    const deleteSchedule = (id) => {
        const filtered = settings.wwcc_report_schedule.filter(s => s.id !== id);
        setSettings({ ...settings, wwcc_report_schedule: filtered });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>System Settings</Typography>

            <DragDropContext onDragEnd={onDragEnd}>


                {/* Awards Module */}
                {isModuleEnabled('awards') && (
                    <>
                        {/* Ceremony Timelines */}
                        <Accordion
                            expanded={expanded === 'ceremony'}
                            onChange={handleAccordionChange('ceremony')}
                            sx={accordionStyle}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Ceremony Timelines</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Open Date Delay" type="number" fullWidth value={settings.ceremony_open_delay_weeks} onChange={(e) => setSettings({ ...settings, ceremony_open_delay_weeks: e.target.value })} slotProps={{ input: { endAdornment: <InputAdornment position="end">weeks</InputAdornment> } }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Cut-off Date Delay" type="number" fullWidth value={settings.ceremony_cutoff_delay_weeks} onChange={(e) => setSettings({ ...settings, ceremony_cutoff_delay_weeks: e.target.value })} slotProps={{ input: { endAdornment: <InputAdornment position="end">weeks</InputAdornment> } }} />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        {/* Student Data Headings */}
                        {renderSchemaEditor('student_import_schema', 'Student Data Headings', 'Headings for Student CSV imports.')}

                        {/* Award Data Headings                 */}
                        {renderSchemaEditor('award_import_schema', 'Award Data Headings', 'Headings for Awards/Prizes CSV imports.')}
                    </>
                )}

                {/* WWCC Module */}
                {isModuleEnabled('wwcc') && (
                    <>
                        {/* WWCC Module */}
                        {isModuleEnabled('wwcc') && (
                            <Accordion
                                expanded={expanded === 'wwcc'}
                                onChange={handleAccordionChange('wwcc')}
                                sx={accordionStyle}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">WWCC Expiry Notifications</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Set the intervals for when administrators should receive expiry alerts.
                                    </Typography>

                                    {settings.wwcc_alert_settings.map((notif) => (
                                        <Box key={notif.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>

                                            <Box sx={{ width: 140, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ marginLeft: '10px', fontWeight: 400, color: 'text.primary' }}>
                                                    Notification email
                                                </Typography>
                                            </Box>

                                            <TextField
                                                type="number"
                                                size="small"
                                                value={notif.value}
                                                onChange={(e) => updateNotification(notif.id, 'value', e.target.value)}
                                                sx={{ width: 80 }}
                                            />

                                            <TextField
                                                select
                                                size="small"
                                                value={notif.unit}
                                                onChange={(e) => updateNotification(notif.id, 'unit', e.target.value)}
                                                sx={{ width: 120 }}
                                                SelectProps={{ native: true }}
                                            >
                                                <option value="days">days</option>
                                                <option value="weeks">weeks</option>
                                            </TextField>

                                            <Box sx={{ width: 160, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ marginLeft: '10px', fontWeight: 400, color: 'text.primary' }}>
                                                    prior to WWCC expiry
                                                </Typography>
                                            </Box>

                                            <IconButton onClick={() => deleteNotification(notif.id)} color="inherit" size="small">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addNotification}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mt: 1, fontWeight: 'bold' }}
                                    >
                                        Add notification
                                    </Button>


                                    <Typography variant="subtitle2" sx={{ mt: 4, mb: 1, fontWeight: 'bold' }}>
                                        Report Delivery Schedule
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Define when the automated expiry reports should be emailed to administrators.
                                    </Typography>

                                    {reportSchedule.map((sched) => (
                                        <Box key={sched.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                                            <TextField
                                                select
                                                size="small"
                                                label="Frequency"
                                                value={sched.type}
                                                onChange={(e) => updateSchedule(sched.id, 'type', e.target.value)}
                                                sx={{ width: 130 }}
                                                SelectProps={{ native: true }}
                                            >
                                                {SCHEDULE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </TextField>

                                            {sched.type === 'weekly' ? (
                                                <TextField
                                                    select
                                                    size="small"
                                                    label="On"
                                                    value={sched.day}
                                                    onChange={(e) => updateSchedule(sched.id, 'day', e.target.value)}
                                                    sx={{ width: 140 }}
                                                    SelectProps={{ native: true }}
                                                >
                                                    {DAYS_OF_WEEK.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                                </TextField>
                                            ) : (
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    label="Day of Month"
                                                    value={sched.day}
                                                    onChange={(e) => updateSchedule(sched.id, 'day', e.target.value)}
                                                    InputProps={{ inputProps: { min: 1, max: 28 } }}
                                                    sx={{ width: 140 }}
                                                />
                                            )}

                                            <TextField
                                                type="time"
                                                size="small"
                                                label="At"
                                                value={sched.time}
                                                onChange={(e) => updateSchedule(sched.id, 'time', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ width: 120 }}
                                            />

                                            <IconButton onClick={() => deleteSchedule(sched.id)} color="inherit" size="small">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addSchedule}
                                        variant="outlined"
                                        size="small"
                                    >
                                        Add Schedule Entry
                                    </Button>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </>
                )}

            </DragDropContext>

            {isSuperuser && (
                <Accordion
                    expanded={expanded === 'smtp_test'}
                    onChange={handleAccordionChange('smtp_test')}
                    sx={accordionStyle}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Email Preview (only visible to super-users)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Send a test email to verify your SMTP server configuration is correct.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <TextField
                                label="Recipient Email Address"
                                size="small"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                placeholder="e.g. admin@school.edu.au"
                                sx={{ width: 300 }}
                            />

                            <TextField
                                label="Records expiring in"
                                type="number"
                                size="small"
                                value={testDays}
                                onChange={(e) => setTestDays(e.target.value)}
                                sx={{ width: 150 }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                                }}
                            />

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                <Button
                                    onClick={() => triggerEmailTest('expiry')}
                                    variant="contained"
                                    startIcon={<SendIcon />}
                                    color="primary"
                                    disabled={!testEmail || testing}
                                >
                                    {testing ? <CircularProgress size={24} /> : 'Preview Expiry Report'}
                                </Button>
                            </Box>
                        </Box>

                    </AccordionDetails>
                </Accordion>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ px: 4 }}
                >
                    {saving ? 'Saving...' : 'Save All Settings'}
                </Button>
            </Box>
        </Container>
    );
}